<template>
    <div id="manage-variables" class="p-2">
        <div class="templates-header">
            <h2 class="d-none d-sm-block">
                {{
                    $t("entities.templates.edit-template", {
                        title: selectedTemplate.title,
                    })
                }}
            </h2>
            <p class="d-none d-sm-block">
                {{ $t("entities.templates.message-preview") }}
            </p>

            <perfect-scrollbar class="scroll-area">
                <b-card bg-variant="whatsapp-bg p-1" no-body>
                    <div>
                        <span v-for="part in messageParts">
                            <pre class="whatsapp-bg d-inline bg-none">{{ part.message }}</pre>
                            <select-variable
                                v-if="part.variable"
                                :include="['default']"
                                :value="part.variable"
                                variables-only
                                @input="(variable) => setVariable(part, variable)"
                            />
                        </span>
                    </div>
                </b-card>

                <div class="templates-footer">
                    <b-row>
                        <b-col cols="6">
                            <b-button type="submit" variant="light" @click="$emit('reset-template')">
                                {{ $t("Back") }}
                            </b-button>
                        </b-col>
                        <b-col cols="6">
                            <b-button class="float-right" type="submit" variant="primary" @click.prevent="submit">
                                {{ $t("Save") }}
                            </b-button>
                        </b-col>
                    </b-row>
                </div>
            </perfect-scrollbar>
        </div>
    </div>
</template>

<script>
import SelectVariable from "@/components/selectVariable.vue";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates";
import useFileUploads from "@/composables/useFileUploads";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { required } from "@validations";
import { computed, ref } from "@vue/composition-api";
import {
    BButton,
    BCard,
    BCardBody,
    BCardText,
    BCol,
    BForm,
    BFormFile,
    BFormGroup,
    BFormInput,
    BRow,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        SelectVariable,
        ValidationProvider,
        ValidationObserver,
        required,
        BCardText,
        BFormInput,
        BFormGroup,
        BForm,
        BFormFile,
        BRow,
        BCol,
        BButton,
        BCard,
        BCardBody,
    },
    props: {
        selectedTemplate: {
            type: Object,
            required: true,
        },
    },
    methods: {
        changeVariableValue(event) {
            const selectedDropdownIndex = parseInt(event.target.attributes["data-index"].value);

            this.selectedTemplate.variables[selectedDropdownIndex - 1] = {
                index: selectedDropdownIndex,
                value: event.target.value,
            };

            this.selectedTemplate.content = this.selectedTemplate.indexed;

            this.selectedTemplate.content = this.selectedTemplate.content.replace(/{{\s*([^}]*)\s*}}/g, (_, $1) => {
                return this.selectedTemplate.variables[$1 - 1].value;
            });
        },
    },
    setup(props, { emit }) {
        const toast = useToast();

        const { fetchVariablesFromContent, variablesToIndex, availableVariables } = useActiveChatTemplates();
        const { getAllowedFileTypes } = useFileUploads();
        const selectedTemplateString = ref("");

        props.selectedTemplate.indexed = variablesToIndex(props.selectedTemplate.content);

        const submit = () => {
            axios
                .patch(`templates/${props.selectedTemplate.id}`, {
                    content: props.selectedTemplate.content,
                })
                .then(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.templates.approved-templates"),
                            text: i18n.t("toast.update.success", { entity: i18n.t("entities.templates.template") }),
                            icon: "check",
                            variant: "success",
                        },
                    });

                    emit("reset-template");
                })
                .catch(() =>
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.templates.approved-templates"),
                            text: i18n.t("toast.update.error", { entity: i18n.t("entities.templates.template") }),
                            icon: "times",
                            variant: "danger",
                        },
                    })
                );
        };

        const setVariableDropdowns = () => {
            const variables = fetchVariablesFromContent(props.selectedTemplate.content);
            selectedTemplateString.value = props.selectedTemplate.content;
        };

        const messageParts = computed({
            get: () => {
                const regex = /{{[^}]+}}/g;

                let match;
                let previousIndex = 0;
                const messageParts = [];

                while ((match = regex.exec(props.selectedTemplate.content)) !== null) {
                    messageParts.push({
                        message: props.selectedTemplate.content.substring(previousIndex, match.index),
                        variable: match[0],
                    });

                    previousIndex = match.index + match[0].length;
                }

                messageParts.push({
                    message: props.selectedTemplate.content.substring(previousIndex),
                });

                return messageParts;
            },
            set: (value) => {
                props.selectedTemplate.content = value.reduce(
                    (message, variable) => message + variable.message + (variable.variable ?? ""),
                    ""
                );
            },
        });

        const setVariable = (messagePart, variable) => {
            messagePart.variable = variable;
            messageParts.value = [...messageParts.value];
        };

        setVariableDropdowns();
        const allowedFileTypes = computed(() => getAllowedFileTypes(props.selectedTemplate.mediaType, "whatsapp"));

        const openFilePrompt = () => {
            document.getElementById("file").click();
        };

        return {
            setVariable,
            messageParts,
            selectedTemplateString,
            fetchVariablesFromContent,
            submit,

            allowedFileTypes,
            openFilePrompt,
        };
    },
};
</script>
